import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns/minutesToMilliseconds";

import { tenantKeys } from "~api/tenants.ts";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import { FeaturesService } from "~generated";

const featureKeys = {
  base: (tenantId: string) =>
    [...tenantKeys.detail(tenantId), "features", "v1"] as const,
  list: (tenantId: string) => [...featureKeys.base(tenantId), "list"] as const,
};

export function featuresQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: featureKeys.list(tenantId),
    queryFn: () => getFeatures(tenantId),
    // Preloaded above the router; the gate and every useFeatures observer mount later
    staleTime: minutesToMilliseconds(1),
  });
}

export function useFeatures() {
  const currentTenant = useCurrentTenant();

  return useQuery({
    ...featuresQueryOptions(currentTenant.id),
    // Without this, RequireFeaturesLoaded sees `isPending` on every switch to
    // a not-yet-visited tenant and swaps in a spinner, unmounting (and
    // resetting the local state of) the whole page tree below it.
    placeholderData: keepPreviousData,
  });
}

async function getFeatures(tenantId: string) {
  const features = await FeaturesService.getFeatures(tenantId);
  if (!features) {
    return [];
  }
  return features.features;
}
